import React, { useEffect, useState } from 'react';
import { api, JobSlaDto } from '../../services/api';
import { Card, Btn, Alert, Spinner, C } from '../../components/UI';

// ── Job SLA / Due-Date Tracking (core primitive) ────────────────────────────────
// Visibility-first: an at-risk/overdue list, plus setting a due date on a specific job. No
// notification engine yet — see JobSla's doc comment in Models.cs.

const priorityColors: Record<string, [string, string]> = {
    Normal: ['#F5F5F5', C.textMuted],
    Rush: ['#FEF3C7', '#92400E'],
    Escalated: ['#FFF0F0', '#9B1C1C'],
};

export function JobSlaPanel() {
    const [tab, setTab] = useState<'at-risk' | 'set'>('at-risk');

    return (
        <div style={{ maxWidth: 860 }}>
            <div style={{ marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Job SLA Tracking</h2>
                <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>Due dates and priority per job — see what's at risk before it's missed</div>
            </div>

            <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: `2px solid ${C.border}` }}>
                {([['at-risk', 'At Risk'], ['set', 'Set due date']] as const).map(([key, label]) => (
                    <button key={key} onClick={() => setTab(key)} style={{
                        padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
                        fontSize: 13, fontWeight: 600,
                        color: tab === key ? C.greenMid : C.textSub,
                        borderBottom: tab === key ? `2px solid ${C.greenMid}` : '2px solid transparent',
                        marginBottom: -2,
                    }}>{label}</button>
                ))}
            </div>

            {tab === 'at-risk' && <AtRiskTab />}
            {tab === 'set' && <SetSlaTab />}
        </div>
    );
}

function SlaRow({ s, onEscalate, escalating }: { s: JobSlaDto; onEscalate: () => void; escalating: boolean }) {
    const [bg, fg] = priorityColors[s.priority] || priorityColors.Normal;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px' }}>
            <span style={{
                fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
                background: bg, color: fg, flexShrink: 0, minWidth: 78, textAlign: 'center',
            }}>{s.priority}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{s.serviceTypeName}</div>
                <div style={{ fontSize: 12, color: C.textSub }}>{s.jobStatus} · {s.contractorName || 'Unassigned'}</div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 12, color: s.isOverdue ? '#9B1C1C' : C.textMuted, fontWeight: s.isOverdue ? 700 : 400 }}>
                    {s.isOverdue ? 'Overdue — ' : 'Due '}{new Date(s.dueDate).toLocaleString()}
                </div>
            </div>
            {s.priority !== 'Escalated' && (
                <Btn variant="outline" onClick={onEscalate} disabled={escalating} style={{ flexShrink: 0 }}>
                    {escalating ? '…' : 'Escalate'}
                </Btn>
            )}
        </div>
    );
}

function AtRiskTab() {
    const [slas, setSlas] = useState<JobSlaDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [withinHours, setWithinHours] = useState(24);
    const [escalating, setEscalating] = useState<string | null>(null);

    const load = () => {
        setLoading(true); setError('');
        api.getAtRiskJobSlas(withinHours)
            .then(setSlas)
            .catch(e => setError(e.message || 'Failed to load.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [withinHours]);

    const escalate = async (jobId: string) => {
        setEscalating(jobId);
        try { await api.escalateJobSla(jobId); load(); }
        catch (e: any) { setError(e.message || 'Failed to escalate.'); }
        finally { setEscalating(null); }
    };

    return (
        <div>
            <Alert message={error} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: C.textSub }}>Due within</label>
                <select value={withinHours} onChange={e => setWithinHours(Number(e.target.value))}
                    style={{ padding: '6px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: '#fff' }}>
                    <option value={6}>6 hours</option>
                    <option value={24}>24 hours</option>
                    <option value={72}>3 days</option>
                    <option value={168}>7 days</option>
                </select>
                <Btn variant="outline" onClick={load}>Refresh</Btn>
            </div>
            {loading ? <Spinner /> : slas.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 48, color: C.textMuted }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                    <div style={{ fontWeight: 600, color: C.textSub, fontSize: 16 }}>Nothing at risk</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>No jobs due within {withinHours} hours.</div>
                </Card>
            ) : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    {slas.map((s, i) => (
                        <div key={s.jobId} style={{ borderTop: i === 0 ? 'none' : `1px solid ${C.border}` }}>
                            <SlaRow s={s} onEscalate={() => escalate(s.jobId)} escalating={escalating === s.jobId} />
                        </div>
                    ))}
                </Card>
            )}
        </div>
    );
}

function SetSlaTab() {
    const [jobId, setJobId] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState<'Normal' | 'Rush' | 'Escalated'>('Normal');
    const [current, setCurrent] = useState<JobSlaDto | null>(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [msg, setMsg] = useState('');

    const lookup = async () => {
        if (!jobId.trim()) { setError('Enter a job id.'); return; }
        setLoading(true); setError(''); setMsg('');
        try {
            const sla = await api.getJobSla(jobId.trim());
            setCurrent(sla);
            if (sla) { setDueDate(sla.dueDate.slice(0, 16)); setPriority(sla.priority); }
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        if (!jobId.trim() || !dueDate) { setError('Job id and due date are required.'); return; }
        setSaving(true); setError(''); setMsg('');
        try {
            const sla = await api.setJobSla(jobId.trim(), new Date(dueDate).toISOString(), priority);
            setCurrent(sla);
            setMsg('Saved.');
        } catch (e: any) {
            setError(e.message || 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card style={{ maxWidth: 480, padding: 24 }}>
            <Alert message={error} />
            {msg && <div style={{ background: C.greenBg, color: C.greenMid, borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{msg}</div>}

            <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Job id</label>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input value={jobId} onChange={e => setJobId(e.target.value)} placeholder="GUID"
                        style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text }} />
                    <Btn variant="outline" onClick={lookup} disabled={loading}>{loading ? '…' : 'Look up'}</Btn>
                </div>
                {current && <div style={{ fontSize: 12, color: C.textSub, marginTop: 6 }}>{current.serviceTypeName} · {current.jobStatus}</div>}
            </div>

            <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Due date</label>
                <input type="datetime-local" value={dueDate} onChange={e => setDueDate(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Priority</label>
                <select value={priority} onChange={e => setPriority(e.target.value as any)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: '#fff', boxSizing: 'border-box' }}>
                    <option value="Normal">Normal</option>
                    <option value="Rush">Rush</option>
                    <option value="Escalated">Escalated</option>
                </select>
            </div>

            <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save due date'}</Btn>
        </Card>
    );
}
